/**
 * Query keys for every workshop surface, in one module so the chat hook's
 * tool-driven invalidations and the tabs' queries can never disagree.
 */

export const workshopProjectsKey = (brandId: string | null) => [
  "workshopProjects",
  brandId,
];

export const workshopProjectKey = (projectId: string | null) => [
  "workshopProject",
  projectId,
];

/** The owner and the people they added (collab mode). */
export const workshopMembersKey = (projectId: string | null) => [
  "workshopMembers",
  projectId,
];

/** The brand's Quick Create list (what the viewer's role may start). */
export const workshopQuickCreateKey = (brandId: string | null) => ["workshopQuickCreate", brandId];

/** Prefix of every brand's project list. */
export const WORKSHOP_PROJECTS_ROOT = ["workshopProjects"];

export const workshopContextKey = (projectId: string | null) => [
  "workshopContext",
  projectId,
];

/** Prefix key — invalidating it hits both includeArchived variants. */
export const workshopArtifactsKey = (projectId: string | null) => [
  "workshopArtifacts",
  projectId,
];

export const workshopArtifactsListKey = (
  projectId: string | null,
  includeArchived: boolean,
) => ["workshopArtifacts", projectId, includeArchived];

export const workshopArtifactKey = (artifactId: string | null) => [
  "workshopArtifact",
  artifactId,
];

export const workshopArtifactRevisionsKey = (artifactId: string | null) => [
  "workshopArtifactRevisions",
  artifactId,
];

/** Prefix of every artifact's notes in a project — what a notes hint invalidates. */
export const workshopArtifactNotesRootKey = (projectId: string | null) => [
  "workshopArtifactNotes",
  projectId,
];

export const workshopArtifactNotesKey = (projectId: string | null, artifactId: string | null) => [
  "workshopArtifactNotes",
  projectId,
  artifactId,
];

/** Prefix key — invalidating it hits both includeArchived variants. */
export const workshopIdeasKey = (projectId: string | null) => [
  "workshopIdeas",
  projectId,
];

export const workshopIdeasListKey = (
  projectId: string | null,
  includeArchived: boolean,
) => ["workshopIdeas", projectId, includeArchived];

export const workshopIdeaKey = (ideaId: string | null) => [
  "workshopIdea",
  ideaId,
];

export const workshopSessionsKey = (projectId: string | null) => [
  "workshopSessions",
  projectId,
];

export const workshopDeploymentsKey = (projectId: string | null) => [
  "workshopDeployments",
  projectId,
];

/** The project's own images — never the whole brand library. */
export const workshopMediaKey = (projectId: string | null) => [
  "workshopMedia",
  projectId,
];

/** Prefix key — invalidating it hits the project list and every thread's strip. */
export const workshopFilesKey = (projectId: string | null) => ["workshopFiles", projectId];

/** Every file the project owns (the Sources tab). */
export const workshopFilesListKey = (projectId: string | null) => ["workshopFiles", projectId, "all"];

/**
 * One thread's attachment strip. Its own branch under the prefix, so a thread
 * never reads the project-wide list from the cache — not even a draft thread
 * with no id yet.
 */
export const workshopThreadFilesKey = (projectId: string | null, sessionId: string | null) => [
  "workshopFiles",
  projectId,
  "thread",
  sessionId,
];

export const workshopDeploymentRunsKey = (deploymentId: string | null) => [
  "workshopDeploymentRuns",
  deploymentId,
];

export const workshopDeploymentArtifactsKey = (deploymentId: string | null) => [
  "workshopDeploymentArtifacts",
  deploymentId,
];

export const workshopDeploymentMessagesKey = (deploymentId: string | null) => [
  "workshopDeploymentMessages",
  deploymentId,
];

export const workshopAgentsKey = ["workshopAgents"];

export const workshopSkillsKey = ["workshopSkills"];
